/**
 * usePhotoSrc — the single path for rendering a PhotoRecord as an <img src>.
 *
 * Precedence (post-M83 / ADR-044):
 * 1. If the row is pendingArrayBufferMigration → state='migrating' (no download,
 *    no error). The hook subscribes to row-mutation events for this photoId and
 *    re-runs when Phase 2 of the v19 migration clears the flag.
 * 2. If blobBytes is present locally → reconstruct Blob + createObjectURL.
 * 3. If remoteUrl is present → call downloadPhoto(), state='loading', then
 *    on success re-read the record and reconstruct.
 * 4. If neither → state='error' with kind='no-remote-url'.
 *
 * The created object URL is revoked on unmount to prevent memory leaks.
 * retry() clears error state and re-attempts the download. Calling retry
 * while a download is already in-flight is a no-op (idempotent).
 *
 * RULE-144: This hook is extracted before any consumer migrates (Phase 4.2).
 * Phase 4.3 migrates all createObjectURL(photo.blob) call sites to use this hook.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

import { getPhoto } from '@/services/photo-service';
import { downloadPhoto, PhotoHydrationError } from '@/services/photo-sync-service';
import { getPhotoMutationBus } from '@/services/storage/dexie-adapter';

export type PhotoSrcState = 'idle' | 'migrating' | 'loading' | 'ready' | 'error';

export interface UsePhotoSrcResult {
  src: string | undefined;
  state: PhotoSrcState;
  error?: PhotoHydrationError;
  retry: () => void;
}

export function usePhotoSrc(photoId: string | undefined): UsePhotoSrcResult {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [state, setState] = useState<PhotoSrcState>('idle');
  const [error, setError] = useState<PhotoHydrationError | undefined>(undefined);

  // Track the current object URL so we can revoke it on cleanup
  const objectUrlRef = useRef<string | undefined>(undefined);
  // Prevent double-download if an in-flight request is already running
  const downloadingRef = useRef(false);
  // Increment to trigger a re-run of the effect (used by retry())
  const [retryKey, setRetryKey] = useState(0);
  // Increment when a photo-mutation event for this photoId fires while migrating
  const [migrationKey, setMigrationKey] = useState(0);

  const revokeCurrent = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    if (!photoId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- early-return branch clears state before async path
      setState('idle');
      return;
    }

    let cancelled = false;

    async function load() {
      if (!photoId) return;

      // Read the current local record
      const photo = await getPhoto(photoId);

      if (cancelled) return;

      if (!photo) {
        setState('error');
        setError(
          new PhotoHydrationError(
            `usePhotoSrc: photo ${photoId} not found in local store`,
            'not-found',
          ),
        );
        return;
      }

      // M83 / ADR-044 §F: pending v19 migration takes precedence over byte reads.
      // The row still carries the legacy `blob` field but blobBytes is unset; the
      // Phase 2 processor will populate blobBytes and clear the flag. Showing a
      // 'migrating' placeholder prevents a wasted download or a false no-remote-url
      // error during the race window.
      if (photo.pendingArrayBufferMigration === true) {
        setState('migrating');
        setError(undefined);
        return;
      }

      if (photo.blobBytes) {
        // Bytes are already present — reconstruct Blob + URL immediately
        revokeCurrent();
        const reconstructed = new Blob([photo.blobBytes], {
          type: photo.blobType ?? 'image/jpeg',
        });
        const url = URL.createObjectURL(reconstructed);
        objectUrlRef.current = url;
        setSrc(url);
        setState('ready');
        setError(undefined);
        return;
      }

      if (!photo.remoteUrl) {
        setState('error');
        setError(
          new PhotoHydrationError(
            `usePhotoSrc: photo ${photoId} has no blobBytes and no remoteUrl`,
            'no-remote-url',
          ),
        );
        return;
      }

      // Bytes absent but remoteUrl present — download on demand
      if (downloadingRef.current) return; // In-flight for this photoId — wait
      downloadingRef.current = true;
      setState('loading');

      try {
        const blob = await downloadPhoto(photoId);

        if (cancelled) return;

        revokeCurrent();
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setSrc(url);
        setState('ready');
        setError(undefined);
      } catch (err) {
        if (cancelled) return;
        const hydrationError =
          err instanceof PhotoHydrationError
            ? err
            : new PhotoHydrationError(
                `usePhotoSrc: download failed for ${photoId}: ${String(err)}`,
                'unknown',
              );
        setState('error');
        setError(hydrationError);
      } finally {
        downloadingRef.current = false;
      }
    }

    load().catch((err: unknown) => {
      if (!cancelled) {
        console.error('[usePhotoSrc] Unexpected error:', err);
        setState('error');
        setError(
          new PhotoHydrationError(
            `usePhotoSrc: unexpected error for ${photoId}: ${String(err)}`,
            'unknown',
          ),
        );
      }
    });

    return () => {
      cancelled = true;
    };
    // retryKey + migrationKey are intentionally in deps — incrementing them re-runs the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoId, retryKey, migrationKey]);

  // Subscribe to the photo-mutation event bus while in the 'migrating' state.
  // Phase 2 of the v19 migration emits 'photo-mutated' with detail.photoId on
  // every successfully-processed row; we re-run the load effect to pick up the
  // newly-populated blobBytes (W-9 event-based notification — ADR-044 §F).
  //
  // WC-1 race fix (ADR-044 reviewer finding): Phase 2 can emit 'photo-mutated'
  // between setState('migrating') in the load effect and this subscription
  // effect committing its addEventListener call. Without the re-fetch below the
  // hook would be stuck in 'migrating' forever because the event was missed.
  //
  // Fix (option b): after attaching the listener, immediately re-read the photo
  // once. If Phase 2 already cleared pendingArrayBufferMigration in the gap,
  // this re-fetch will find the row updated and trigger a migrationKey bump —
  // closing the race window at minimal blast radius (local hook change only,
  // no bus API changes). Aligns with Case 3: the online listener re-runs
  // runPendingPhotoBytesMigration which then emits; without the re-fetch a Case
  // 3 row could also slip past if the re-run completes synchronously before
  // this effect commits.
  useEffect(() => {
    if (!photoId || state !== 'migrating') return;

    let effectCancelled = false;

    const bus = getPhotoMutationBus();
    const handler = (ev: Event) => {
      const customEv = ev as CustomEvent<{ photoId: string }>;
      if (customEv.detail?.photoId === photoId) {
        setMigrationKey((k) => k + 1);
      }
    };
    bus.addEventListener('photo-mutated', handler);

    // Close the race window: re-read the photo once after attaching. If Phase 2
    // emitted between setState('migrating') and here, the row will already have
    // blobBytes set and pendingArrayBufferMigration cleared. We bump migrationKey
    // to re-trigger the main load effect.
    void getPhoto(photoId).then((latestPhoto) => {
      if (!effectCancelled && latestPhoto && !latestPhoto.pendingArrayBufferMigration) {
        setMigrationKey((k) => k + 1);
      }
    });

    return () => {
      effectCancelled = true;
      bus.removeEventListener('photo-mutated', handler);
    };
  }, [photoId, state]);

  // Revoke URL on unmount
  useEffect(() => {
    return () => {
      revokeCurrent();
    };
  }, [revokeCurrent]);

  const retry = useCallback(() => {
    if (downloadingRef.current) return; // Already in-flight
    setError(undefined);
    setState('idle');
    setSrc(undefined);
    setRetryKey((k) => k + 1);
  }, []);

  return { src, state, error, retry };
}
